import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get(`/courses/${slug}`)
      .then((r) => { if (!cancel) setCourse(r.data); })
      .catch(() => { if (!cancel) setCourse(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="course-detail-loading" className="bg-navy pt-44 pb-24 max-w-[1600px] mx-auto px-6 md:px-12 text-[#8A99AA] font-light">
        Loading...
      </div>
    );
  }
  if (!course) {
    return (
      <div className="bg-navy pt-44 pb-24 max-w-3xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-[#F5F4F0] font-serif">Programme not found</h1>
        <Link to="/courses" className="mt-6 inline-flex items-center gap-2 text-[#C6A87C]">
          <ArrowLeft size={14} /> Back to all programmes
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`course-detail-${course.slug}`} className="bg-navy pt-40 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
        <Reveal y={14}>
          <Link to="/courses" data-testid="back-to-courses" className="inline-flex items-center gap-2 label-overline">
            <ArrowLeft size={14} /> All programmes
          </Link>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-8">
            <Reveal>
              <h1 data-testid="course-title" className="text-5xl lg:text-7xl text-[#F5F4F0] leading-[1.02] tracking-tight">
                {course.title}
              </h1>
              <p className="mt-5 font-serif text-2xl text-[#C6A87C] italic">{course.tagline}</p>
              <p className="mt-8 text-lg text-[#8A99AA] font-light leading-relaxed max-w-2xl">{course.summary}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-16">
                <div className="label-overline mb-7 flex items-center gap-3">
                  <span className="h-px w-8 bg-[#C6A87C]" />
                  What you&rsquo;ll be able to do
                </div>
                <ul className="space-y-4">
                  {course.outcomes.map((o, i) => (
                    <li key={i} data-testid={`course-outcome-${i}`} className="flex gap-3 items-start">
                      <CheckCircle size={22} weight="light" className="text-[#C6A87C] mt-0.5 flex-shrink-0" />
                      <span className="text-[#F5F4F0]/85 font-light">{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-16">
                <div className="label-overline mb-7 flex items-center gap-3">
                  <span className="h-px w-8 bg-[#C6A87C]" />
                  Curriculum modules
                </div>
                <ol className="border-l border-[#C6A87C]/20 space-y-7 pl-6">
                  {course.modules.map((m, i) => (
                    <li key={i} data-testid={`course-module-${i}`} className="relative">
                      <span className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full bg-[#C6A87C] border-4 border-navy" />
                      <div className="font-mono-tech text-xs uppercase tracking-widest text-[#8A99AA]">
                        Module {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="mt-1 font-serif text-2xl text-[#F5F4F0]">{m}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <Reveal delay={0.25}>
              <div className="bg-navy-deep border border-[#C6A87C]/20 p-8">
                <div className="space-y-5">
                  <Row k="Duration" v={course.duration} />
                  <Row k="Level" v={course.level} />
                  <Row k="Format" v={course.format} />
                  <Row k="Mentor" v="1:1 office hours" />
                  <Row k="Course" v="Portfolio artefact" />
                </div>
                <div className="hairline-divider my-7" />
                <Link
                  to={`/enroll/${course.slug}`}
                  data-testid="course-enroll-cta"
                  className="group relative flex items-center justify-center gap-2 w-full h-12 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden"
                >
                  <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                  <span className="relative inline-flex items-center gap-2">
                    Request enrolment <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
                <p className="mt-4 text-xs text-[#8A99AA]/70 text-center font-light">
                  Advisor responds within one business day.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="label-overline !text-[10px]">{k}</span>
      <span className="text-[#F5F4F0] text-sm font-medium text-right">{v}</span>
    </div>
  );
}
